import { lazy, Suspense, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useBlocker } from '@tanstack/react-router';
import { Check, Copy, GitCommitHorizontal, Pencil, RotateCcw, Save, ShieldCheck, X } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Input } from '@/components/ui/Input';
import { ApiError } from '@/lib/api';
import {
  extractPlaybookIssues,
  routineKeys,
  useSavePlaybook,
  useValidatePlaybook,
} from '@/hooks/useRoutines';
import type { PlaybookIssue, RoutineDetail } from '@/hooks/useRoutines';
import { PlaybookIssueList } from './PlaybookIssueList';
import type { PlaybookCodeMirrorHandle } from './PlaybookCodeMirror';

/**
 * PlaybookPanel - Anzeige + Web-Editor für das Playbook einer Routine.
 *
 * Ansicht: CodeMirror read-only (Highlighting + Zeilennummern, Theme-Tokens).
 * Editor: gleicher CodeMirror, plus Validieren/Speichern/Zuruecksetzen,
 * Fehler-Marker an der Zeile, Konflikt-Behandlung (409) und Schutz vor
 * Verlassen mit ungespeicherten Änderungen (Router-Blocker + beforeunload).
 *
 * CodeMirror hängt an einem lazy-Chunk; solange er lädt, zeigt der
 * Fallback den Source als schlichten Pre-Block.
 */

const PlaybookCodeMirror = lazy(() => import('./PlaybookCodeMirror'));

interface Props {
  /** URL-Slug der Detail-Query (Cache-Key). */
  slug: string;
  routine: RoutineDetail;
}

interface LastValidation {
  /** Content-Stand, für den das Ergebnis gilt. */
  content: string;
  valid: boolean;
}

interface LastSave {
  commit: string | null;
  sha256: string;
}

export function PlaybookPanel({ slug, routine }: Props): JSX.Element {
  const qc = useQueryClient();
  const source = routine.playbook_source ?? '';

  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [draft, setDraft] = useState('');
  const [baseline, setBaseline] = useState('');
  const [issues, setIssues] = useState<PlaybookIssue[]>([]);
  const [lastValidation, setLastValidation] = useState<LastValidation | null>(null);
  const [lastSave, setLastSave] = useState<LastSave | null>(null);
  const [commitMessage, setCommitMessage] = useState('');
  const [reloading, setReloading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [knownSha, setKnownSha] = useState<string | null>(null);

  const editorRef = useRef<PlaybookCodeMirrorHandle>(null);

  const validateMut = useValidatePlaybook(routine.code);
  const saveMut = useSavePlaybook(slug, routine.code);

  const dirty = mode === 'edit' && draft !== baseline;
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  // Navigations-Schutz: In-App-Navigation UND Tab-Schliessen/Reload.
  useBlocker({
    shouldBlockFn: () => {
      if (!dirtyRef.current) return false;
      return !window.confirm(
        'Es gibt ungespeicherte Änderungen am Playbook. Seite trotzdem verlassen?'
      );
    },
    enableBeforeUnload: () => dirtyRef.current,
  });

  const saveError = saveMut.error;
  const isConflict = saveError instanceof ApiError && saveError.status === 409;
  const handled400 = extractPlaybookIssues(saveError).length > 0;
  const busy = validateMut.isPending || saveMut.isPending || reloading;

  const enterEdit = (): void => {
    setDraft(source);
    setBaseline(source);
    setIssues([]);
    setLastValidation(null);
    setLastSave(null);
    validateMut.reset();
    saveMut.reset();
    setMode('edit');
  };

  const exitEdit = (): void => {
    if (dirty && !window.confirm('Änderungen verwerfen und zur Ansicht zurückkehren?')) return;
    setIssues([]);
    setLastValidation(null);
    validateMut.reset();
    saveMut.reset();
    setMode('view');
  };

  const resetDraft = (): void => {
    setDraft(baseline);
    setIssues([]);
    setLastValidation(null);
    validateMut.reset();
    saveMut.reset();
  };

  const onValidate = async (): Promise<boolean> => {
    try {
      const res = await validateMut.mutateAsync(draft);
      setIssues(res.issues);
      setLastValidation({ content: draft, valid: res.valid });
      return res.valid;
    } catch {
      // validateMut.error rendert unten als ErrorAlert.
      return false;
    }
  };

  const onSave = async (): Promise<void> => {
    // Erst validieren - spart einen kaputten Commit und hält die Marker aktuell.
    const contentToSave = draft;
    const valid = await onValidate();
    if (!valid) return;
    try {
      const res = await saveMut.mutateAsync({
        content: contentToSave,
        message: commitMessage || null,
        expectedSha256: knownSha,
      });
      setKnownSha(res.sha256);
      setLastSave({ commit: res.commit, sha256: res.sha256 });
      setIssues(res.issues ?? []);
      setBaseline(contentToSave);
      setCommitMessage('');
      setMode('view');
    } catch (err) {
      const fromBody = extractPlaybookIssues(err);
      if (fromBody.length > 0) {
        setIssues(fromBody);
        setLastValidation({ content: contentToSave, valid: false });
      }
      // 409 und andere Fehler rendern unten über saveMut.error.
    }
  };

  const adoptServerState = async (): Promise<void> => {
    setReloading(true);
    try {
      await qc.invalidateQueries({ queryKey: routineKeys.detail(slug) });
      const fresh = qc.getQueryData<RoutineDetail>(routineKeys.detail(slug));
      const freshSource = fresh?.playbook_source ?? '';
      setDraft(freshSource);
      setBaseline(freshSource);
      setKnownSha(null);
      setIssues([]);
      setLastValidation(null);
      saveMut.reset();
    } finally {
      setReloading(false);
    }
  };

  const onCopy = async (): Promise<void> => {
    if (!source) return;
    try {
      await navigator.clipboard.writeText(source);
      setCopied(true);
      setTimeout(() => setCopied(false), 1_500);
    } catch {
      /* Browser hat kein Clipboard - silently ignore. */
    }
  };

  const validationCurrent = lastValidation !== null && lastValidation.content === draft;
  const errorCount = issues.filter((i) => i.severity === 'error').length;
  const warningCount = issues.filter((i) => i.severity === 'warning').length;

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          Playbook
          <Badge tone="neutral">YAML</Badge>
        </span>
      }
      description={
        mode === 'view'
          ? 'Source des Ansible-Playbooks (wird bei Änderungen ins Git committet)'
          : 'Playbook bearbeiten — Speichern validiert und committet ins Git'
      }
      actions={
        mode === 'view' ? (
          <>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Copy className="size-3" />}
              onClick={() => void onCopy()}
              disabled={!source}
            >
              {copied ? 'Kopiert' : 'Kopieren'}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Pencil className="size-3" />}
              onClick={enterEdit}
              disabled={routine.playbook_source == null}
            >
              Bearbeiten
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<ShieldCheck className="size-3" />}
              onClick={() => void onValidate()}
              loading={validateMut.isPending}
              disabled={saveMut.isPending || reloading}
            >
              Validieren
            </Button>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<RotateCcw className="size-3" />}
              onClick={resetDraft}
              disabled={!dirty || busy}
            >
              Zurücksetzen
            </Button>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<X className="size-3" />}
              onClick={exitEdit}
              disabled={busy}
            >
              Abbrechen
            </Button>
            <Button
              size="sm"
              leftIcon={<Save className="size-3" />}
              onClick={() => void onSave()}
              loading={saveMut.isPending}
              disabled={!dirty || validateMut.isPending || reloading}
            >
              Speichern
            </Button>
          </>
        )
      }
    >
      {mode === 'view' && lastSave && (
        <div
          role="status"
          className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-success/40 bg-success/10 p-3 text-sm text-success"
          data-testid="playbook-saved"
        >
          <Check className="size-4 shrink-0" aria-hidden="true" />
          <span className="font-medium">Gespeichert.</span>
          {lastSave.commit ? (
            <span className="inline-flex items-center gap-1">
              <GitCommitHorizontal className="size-4" aria-hidden="true" />
              Git-Commit{' '}
              <code className="font-mono" title={lastSave.commit}>
                {lastSave.commit.slice(0, 7)}
              </code>
            </span>
          ) : (
            <span>Kein Git-Commit erstellt (Inhalt unverändert oder Git inaktiv).</span>
          )}
        </div>
      )}

      {/* Warnungen aus dem Speichern (Save läuft trotz warnings durch). */}
      {mode === 'view' && issues.length > 0 && (
        <PlaybookIssueList issues={issues} className="mb-3" />
      )}

      {mode === 'view' &&
        (routine.playbook_source != null ? (
          <Suspense fallback={<PlainYamlFallback source={source} />}>
            <PlaybookCodeMirror value={source} readOnly ariaLabel="Playbook-Quelltext (YAML, schreibgeschützt)" />
          </Suspense>
        ) : (
          <p className="text-sm text-muted">
            Backend hat kein Playbook-Source für diese Routine geladen.
          </p>
        ))}

      {mode === 'edit' && (
        <div className="flex flex-col gap-3">
          <Suspense fallback={<PlainYamlFallback source={draft} />}>
            <PlaybookCodeMirror
              ref={editorRef}
              value={draft}
              onChange={setDraft}
              issues={issues}
              ariaLabel="Playbook-Quelltext bearbeiten (YAML)"
              focusOnMount
            />
          </Suspense>
          <p className="text-xs text-muted">
            Tab rückt ein · Esc, dann Tab verlässt den Editor
          </p>

          <Input
            label="Commit-Nachricht (optional)"
            value={commitMessage}
            onChange={(e) => setCommitMessage(e.target.value)}
            placeholder={`playbook(${routine.code}): via Web-Editor aktualisiert`}
            disabled={busy}
          />

          {validationCurrent && lastValidation.valid && issues.length === 0 && (
            <p
              role="status"
              className="flex items-center gap-2 text-sm text-success"
              data-testid="playbook-valid"
            >
              <ShieldCheck className="size-4" aria-hidden="true" />
              Validierung OK — keine Fehler gefunden.
            </p>
          )}
          {validationCurrent && issues.length > 0 && (
            <p className="text-sm font-medium">
              {errorCount} Fehler · {warningCount} Warnungen
            </p>
          )}
          {!validationCurrent && lastValidation !== null && (
            <p className="text-xs text-muted">
              Inhalt seit der letzten Validierung geändert — vor dem Speichern wird erneut
              validiert.
            </p>
          )}

          <PlaybookIssueList
            issues={issues}
            onJumpToLine={(line) => editorRef.current?.focusLine(line)}
          />

          {validateMut.isError && <ErrorAlert error={validateMut.error} />}

          {isConflict && (
            <div
              role="alert"
              className="flex flex-col gap-2 rounded-md border border-warning/40 bg-warning/10 p-3 text-sm"
              data-testid="playbook-conflict"
            >
              <p className="font-medium text-warning">Speichern nicht möglich — Konflikt</p>
              <p>
                Das Playbook wurde in der Zwischenzeit an anderer Stelle geändert (z.B. direkt
                im Git oder in einer zweiten Sitzung). Deine Änderungen wurden <strong>nicht</strong>{' '}
                gespeichert.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => void adoptServerState()}
                  loading={reloading}
                >
                  Server-Stand laden (verwirft meine Änderungen)
                </Button>
                <Button variant="ghost" size="sm" onClick={() => saveMut.reset()}>
                  Weiter bearbeiten
                </Button>
              </div>
            </div>
          )}

          {saveError && !isConflict && !handled400 && <ErrorAlert error={saveError} />}
        </div>
      )}
    </Card>
  );
}

/** Fallback während der CodeMirror-Chunk lädt: schlichter, Theme-faehiger Pre-Block. */
function PlainYamlFallback({ source }: { source: string }): JSX.Element {
  return (
    <pre className="max-h-[600px] overflow-auto rounded-md border border-border bg-surface-2 p-4 font-mono text-xs leading-relaxed text-text">
      <code>{source}</code>
    </pre>
  );
}
